// Cryptographically secure randomness helpers.
// All randomness comes from the Web Crypto CSPRNG. Rejection sampling avoids the
// modulo bias a naive `random % n` would introduce (see plan FINDING-9 for the
// 65536-byte getRandomValues quota — we request in small chunks).

const MAX_BYTES_PER_CALL = 256;

function fillRandomBytes(out: Uint8Array): void {
  for (let offset = 0; offset < out.length; offset += MAX_BYTES_PER_CALL) {
    const chunk = out.subarray(offset, offset + MAX_BYTES_PER_CALL);
    crypto.getRandomValues(chunk);
  }
}

/** Returns `count` cryptographically random bytes. */
export function randomBytes(count: number): Uint8Array {
  if (!Number.isInteger(count) || count < 0) {
    throw new Error(`randomBytes: invalid count ${count}`);
  }
  const out = new Uint8Array(count);
  fillRandomBytes(out);
  return out;
}

/**
 * Uniform random integer in [0, max) without modulo bias.
 * Uses rejection sampling over the largest multiple of `max` that fits in a byte
 * range; for max > 256 it widens to as many bytes as needed.
 */
export function secureRandomInt(max: number): number {
  if (!Number.isInteger(max) || max <= 0) {
    throw new Error(`secureRandomInt: max must be a positive integer, got ${max}`);
  }
  if (max === 1) return 0;

  const bytesNeeded = Math.ceil(Math.log2(max) / 8);
  const range = 256 ** bytesNeeded;
  // Largest multiple of max that fits in `range`; reject anything above it.
  const limit = range - (range % max);

  const buf = new Uint8Array(bytesNeeded);
  // Bounded retries are statistically near-instant; cap to fail fast on bugs.
  for (let attempt = 0; attempt < 1000; attempt++) {
    fillRandomBytes(buf);
    let value = 0;
    for (let i = 0; i < bytesNeeded; i++) {
      value = value * 256 + buf[i];
    }
    if (value < limit) {
      return value % max;
    }
  }
  throw new Error('secureRandomInt: exceeded rejection-sampling retry budget');
}

/** Returns a uniformly random element of a non-empty array. */
export function randomChoice<T>(items: readonly T[]): T {
  if (items.length === 0) {
    throw new Error('randomChoice: empty array');
  }
  return items[secureRandomInt(items.length)];
}

/** In-place Fisher-Yates shuffle using the CSPRNG. */
export function secureShuffle<T>(items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = secureRandomInt(i + 1);
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}
